import React, {
  Dispatch,
  SetStateAction,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';
import { Badge, FormSection } from '@praktio/common-ui';
import { cloneDeep, isEqual, sortBy } from 'lodash';
import { Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { RootState } from '../../../ducks/store';
import { updateTeamManagerAssignments } from '../../../ducks/teams';
import { Days, NotificationFrequency, Roles } from '../../../utils/constants';
import { updateSearchParams } from '../../../utils/functions';
import { Table } from '../../common/Table/Table';

interface ITeamManagersTabProps {
  editingManagerAssignments: boolean;
  teamId: string;
  teamManagers: IUser[];
  notifyHasChanges: (changes: boolean) => void;
  onRowSelect?: (users: IUser[]) => void;
  setEditingManagerAssignments: Dispatch<SetStateAction<boolean>>;
  setRightPaneOpen: Dispatch<SetStateAction<boolean>>;
  setSavedChanges: (success: boolean) => void;
}

const TeamManagersTab = forwardRef<IForwardProps, ITeamManagersTabProps>(
  (
    {
      editingManagerAssignments,
      teamId,
      teamManagers,
      notifyHasChanges,
      onRowSelect,
      setEditingManagerAssignments,
      setRightPaneOpen,
      setSavedChanges,
    },
    ref,
  ) => {
    useImperativeHandle(ref, () => ({
      save,
      cancel,
    }));

    const [searchParams, setSearchParams] = useSearchParams();
    const selectedManager = searchParams.get('userId') || undefined;

    const dispatch = useDispatch();

    const { allTeams, nonTeamManagersInitial, allUsers, learners } = useSelector(
      (state: RootState) => {
        const { teams, users } = state;
        const allUsers = users.list || [];
        const allTeams = teams.list || [];
        const { current: team } = teams;
        const learners: IUser[] =
          team?.users?.filter((user) => {
            return user.roles?.find(
              (role) => role.teamId === teamId && role.role === Roles.Learner,
            );
          }) || [];

        let nonTeamManagersInitial: IUser[] = [];
        allUsers.forEach((userToCheck) => {
          if (!teamManagers.some((teamManager) => teamManager._id === userToCheck._id)) {
            nonTeamManagersInitial.push(userToCheck);
          }
        });
        return { allTeams, nonTeamManagersInitial, allUsers, learners };
      },
    );

    const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
    const [managersToAssign, setManagersToAssign] = useState<string[]>([]);
    const [managersToUnassign, setManagersToUnassign] = useState<string[]>([]);
    const [assignedManagers, setAssignedManagers] = useState<IUser[]>([]);
    const [unassignedManagers, setUnassignedManagers] = useState<IUser[]>([]);
    const [hasChanges, setHasChanges] = useState(false);
    const prevAssignedManagers = useRef<IUser[]>(teamManagers);
    const prevUnassignedManagers = useRef<IUser[]>(nonTeamManagersInitial);
    const prevHasChanges = useRef<boolean>(hasChanges);

    useEffect(() => {
      if (
        (teamManagers && !isEqual(prevAssignedManagers.current, teamManagers)) ||
        (nonTeamManagersInitial && !isEqual(prevUnassignedManagers.current, nonTeamManagersInitial))
      ) {
        setAssignedManagers(teamManagers);
        setUnassignedManagers(nonTeamManagersInitial);
        setManagersToAssign([]);
        setManagersToUnassign([]);
        if (notifyHasChanges) {
          setHasChanges(false);
          notifyHasChanges(false);
        }

        prevAssignedManagers.current = cloneDeep(teamManagers);
        prevUnassignedManagers.current = cloneDeep(nonTeamManagersInitial);
      }
    }, [teamManagers, nonTeamManagersInitial, notifyHasChanges]);

    useEffect(() => {
      if (managersToAssign.length > 0 || managersToUnassign.length > 0) {
        setHasChanges(true);
        notifyHasChanges(true);
      } else {
        setHasChanges(false);
        notifyHasChanges(false);
      }
    }, [managersToAssign, managersToUnassign, notifyHasChanges]);

    useEffect(() => {
      if (!editingManagerAssignments) {
        setAssignedManagers(prevAssignedManagers.current);
        setUnassignedManagers(prevUnassignedManagers.current);
      }
    }, [editingManagerAssignments]);

    useEffect(() => {
      setHiddenColumns(
        editingManagerAssignments ? ['includeSubteams', 'inherited', 'notification'] : [],
      );
    }, [editingManagerAssignments]);

    useEffect(() => {
      if (prevHasChanges.current === false && hasChanges === true) {
        setSavedChanges(false);
      }
      prevHasChanges.current = hasChanges;
    }, [hasChanges, setSavedChanges]);

    const save = async () => {
      try {
        await dispatch(updateTeamManagerAssignments(teamId, managersToUnassign, managersToAssign));
        setManagersToAssign([]);
        setManagersToUnassign([]);
        setSavedChanges(true);
        setEditingManagerAssignments(false);
      } catch (e) {
        console.error(`Couldn't save managers:`, e);
      }
    };

    const cancel = () => {
      setManagersToAssign([]);
      setManagersToUnassign([]);
      setEditingManagerAssignments(false);
    };

    const handleManagerAssignmentChange = ({
      assigning,
      source,
      target,
      user,
    }: {
      assigning: boolean;
      source: IUser[];
      target: IUser[];
      user: IUser;
    }) => {
      const updatedSource = cloneDeep(source);
      const updatedTarget = cloneDeep(target);

      const sourceIndex = updatedSource.findIndex((sourceUser) => sourceUser._id === user._id);
      if (sourceIndex > -1) {
        updatedSource.splice(sourceIndex, 1);
      }

      const targetIndex = updatedTarget.findIndex((targetUser) => targetUser._id === user._id);
      if (targetIndex === -1) {
        updatedTarget.push(user);
      }

      const newManagersToAssign = cloneDeep(managersToAssign);
      const newManagersToUnassign = cloneDeep(managersToUnassign);

      const userPresentInAssigned = prevAssignedManagers.current?.some(
        (prevManager) => prevManager._id === user._id,
      );
      const userPresentInUnassigned = prevUnassignedManagers.current?.some(
        (prevUnassigned) => prevUnassigned._id === user._id,
      );

      if (assigning) {
        const indexInManagersToUnassign = newManagersToUnassign.findIndex(
          (managerToUnassign) => managerToUnassign === user._id,
        );
        if (indexInManagersToUnassign > -1) {
          newManagersToUnassign.splice(indexInManagersToUnassign, 1);
        }
        if (!userPresentInAssigned) {
          newManagersToAssign.push(user._id as string);
        }
      }
      if (!assigning) {
        const indexInManagersToAssign = newManagersToAssign.findIndex(
          (userToAssign) => userToAssign === user._id,
        );
        if (indexInManagersToAssign > -1) {
          newManagersToAssign.splice(indexInManagersToAssign, 1);
        }
        if (!userPresentInUnassigned) {
          newManagersToUnassign.push(user._id as string);
        }
      }

      setManagersToAssign(newManagersToAssign);
      setManagersToUnassign(newManagersToUnassign);

      return { updatedSource, updatedTarget };
    };

    const onManagerAssignmentChange = (user: IUser, assigningToTeam: boolean) => {
      const results = handleManagerAssignmentChange({
        assigning: assigningToTeam,
        source: assigningToTeam ? unassignedManagers : assignedManagers,
        target: assigningToTeam ? assignedManagers : unassignedManagers,
        user: user,
      });

      setAssignedManagers(assigningToTeam ? results.updatedTarget : results.updatedSource);
      setUnassignedManagers(assigningToTeam ? results.updatedSource : results.updatedTarget);
    };

    const processedManagers = assignedManagers.map((teamManager) => {
      const { roles, reportNotification } = teamManager;
      const role = roles?.find(
        (role) =>
          role.role === Roles.TeamManager && (role.teamId === teamId || role.subteamId === teamId),
      );
      const includeSubteams = !!role?.includeSubteams ? 'Include' : 'Not Included';
      let notification: string | number | undefined = reportNotification?.frequency;
      const inherited = !!(role?.subteamId === teamId);
      const inheritedFrom = allTeams.find((team) => team._id === role?.teamId);
      const inheritedFromString = inherited ? inheritedFrom?.name : 'This Team';
      if (reportNotification) {
        const { frequency, day, date } = reportNotification;
        switch (frequency) {
          case NotificationFrequency.Never:
            notification = `Never`;
            break;
          case NotificationFrequency.Weekly:
            notification = `Weekly (${Object.keys(Days).find((daysKey) => Days[daysKey] === day)})`;
            break;
          case NotificationFrequency.Monthly:
            notification = `Monthly (${date})`;
            break;
          case NotificationFrequency.Daily:
            notification = 'Daily';
            break;
          default:
            break;
        }
      }

      return {
        ...teamManager,
        includeSubteams,
        notification,
        inherited: inheritedFromString,
        markedForRemoval: false,
        isChanged: !!managersToAssign.find((userId) => userId === teamManager._id),
      };
    });

    const processedNonManagers = unassignedManagers.map((nonTeamManager) => {
      return {
        ...nonTeamManager,
        isChanged: false,
        markedForRemoval: !!managersToUnassign.find((userId) => userId === nonTeamManager._id),
      };
    });

    const TeamManagersHeaders = [
      {
        Header: 'First Name',
        accessor: 'firstName',
        className: 'TeamManagers-first-name',
        minWidth: 150,
        maxWidth: 300,
        textOverflow: 'ellipsis',
        overflow: 'hidden',
      },
      {
        Header: 'Last Name',
        accessor: 'lastName',
        className: 'TeamManagers-last-name',
        minWidth: 150,
        maxWidth: 300,
        textOverflow: 'ellipsis',
        overflow: 'hidden',
      },
      {
        Header: 'Email',
        accessor: 'email',
        className: 'TeamManagers-email',
        Cell: (props: any) => {
          const original = props.row.original;
          const email = original.email;
          const customDataForCell = props.customDataForCell;
          const editingManagerAssignments = customDataForCell.editingManagerAssignments;
          const managerAssignedToTeam = customDataForCell.managerAssignedToTeam;
          const onManagerAssignmentChange = customDataForCell.onManagerAssignmentChange;

          return (
            <div className="ManagersEmailCell d-flex justify-content-between">
              <span>{email ? email : ''}</span>
              {editingManagerAssignments && (
                <span
                  className="material-icons edit-assignment-status-button pointer"
                  onClick={() => {
                    if (onManagerAssignmentChange) {
                      onManagerAssignmentChange(original, managerAssignedToTeam ? false : true);
                    }
                  }}
                >
                  {managerAssignedToTeam ? 'close' : 'add'}
                </span>
              )}
            </div>
          );
        },
      },
      {
        Header: 'Include Subteams',
        accessor: 'includeSubteams',
        className: 'TeamManagers-include-subteams',
      },
      {
        Header: 'Assigned From',
        accessor: 'inherited',
        className: 'TeamManagers-assigned-from',
      },
      {
        Header: 'Notification',
        accessor: 'notification',
        className: 'TeamManagers-notification',
      },
    ];

    if (!editingManagerAssignments) {
      TeamManagersHeaders.push({
        Header: 'Learners in Report',
        accessor: 'learnerAssignments',
        className: 'TeamManagers-learners',
        Cell: (props: any) => {
          const { row } = props;
          const { original } = row;
          const { learnerAssignments } = original as IUser;
          const teamLearnerAssignments =
            learnerAssignments?.filter((a) => a.teamId === teamId) || [];
          let details: JSX.Element | JSX.Element[] = <div>All learners in team</div>;
          if (teamLearnerAssignments.length && allUsers.length) {
            const existingUsers = teamLearnerAssignments
              .map((a) => learners.find((u) => u._id === a.userId))
              .filter((u) => !!u);

            // If true, then the Manager has Learners assigned to it, but none of them are in the system
            if (existingUsers.length === 0) {
              details = (
                <div className="no-learners">
                  <i className="material-icons">error_outline</i>
                  <span>No learners</span>
                </div>
              );
            } else {
              const sortedUsers = sortBy(existingUsers, 'lastName') as IUser[];
              details = sortedUsers.map((user: IUser) => {
                return (
                  <Badge className="outlined TeamManager-learner-badge">
                    <span>
                      {user.firstName} {user.lastName}
                    </span>
                  </Badge>
                );
              });
            }
          }

          return (
            <div className="TeamManagers-learners-cell">
              <div className="TeamManagers-learners-names">{details}</div>
              <span
                onClick={(e: any) => {
                  e.stopPropagation();
                  setRightPaneOpen(true);
                  updateSearchParams({
                    fields: [
                      {
                        name: 'userId',
                        newValue: row.original._id,
                      },
                      {
                        name: 'task',
                        newValue: 'learnerAssignments',
                      },
                    ],
                    searchParams: searchParams,
                    setSearchParams: setSearchParams,
                  });
                }}
                className="material-icons material-icons-outlined TeamManagers-edit-learners"
              >
                edit
              </span>
            </div>
          );
        },
      });
    }

    return (
      <FormSection sectionName="Managers">
        <Row className="Managers-tables">
          <Col xs={12} md={{ span: editingManagerAssignments ? 6 : 12 }}>
            <Table
              activeRowId={selectedManager}
              columns={TeamManagersHeaders}
              data={processedManagers}
              tableTitle="Assigned Managers"
              hiddenColumns={hiddenColumns}
              customDataForCell={{
                managerAssignedToTeam: true,
                editingManagerAssignments: editingManagerAssignments,
                onManagerAssignmentChange: onManagerAssignmentChange,
              }}
              rowsNotClickable={editingManagerAssignments}
              onRowClick={(row) => {
                setRightPaneOpen(true);
                updateSearchParams({
                  fields: [
                    {
                      name: 'userId',
                      newValue: row.original._id,
                    },
                    {
                      name: 'task',
                      remove: true,
                    },
                  ],
                  searchParams: searchParams,
                  setSearchParams: setSearchParams,
                });
              }}
            />
          </Col>
          <Col xs={12} md={6}>
            {editingManagerAssignments && (
              <Table
                columns={TeamManagersHeaders}
                data={processedNonManagers as IUser[]}
                hiddenColumns={['includeSubteams', 'inherited', 'notification']}
                tableTitle="Unassigned Managers"
                rowsNotClickable={true}
                customDataForCell={{
                  editingManagerAssignments: editingManagerAssignments,
                  managerAssignedToTeam: false,
                  onManagerAssignmentChange: onManagerAssignmentChange,
                }}
              />
            )}
          </Col>
        </Row>
      </FormSection>
    );
  },
);

export default TeamManagersTab;
